import * as React from "react";

import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import { Field, FieldError, FieldGroup, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";

type InviteMemberOption = { value: string; label: string };

type InviteMemberValues = { role: string; accessMode: string; firstName: string; lastName: string; email: string };

type InviteMemberErrors = Partial<Record<keyof InviteMemberValues, string>>;

type InviteMemberResult = { ok: true } | { ok: false; errors?: InviteMemberErrors };

type InviteMemberLabels = {
  title: string;
  description: string;
  role: string;
  rolePlaceholder: string;
  access: string;
  accessPlaceholder: string;
  firstName: string;
  firstNamePlaceholder: string;
  lastName: string;
  lastNamePlaceholder: string;
  email: string;
  emailPlaceholder: string;
  submit: string;
  cancel: string;
  close: string;
  loading: string;
};

const defaultLabels: InviteMemberLabels = {
  title: "Invite a member",
  description: "They get an email with a link to join your organization.",
  role: "Role",
  rolePlaceholder: "Choose a role",
  access: "Access",
  accessPlaceholder: "Choose access",
  firstName: "First name",
  firstNamePlaceholder: "Amira",
  lastName: "Last name",
  lastNamePlaceholder: "Kovač",
  email: "Email",
  emailPlaceholder: "name@company.com",
  submit: "Send invitation",
  cancel: "Cancel",
  close: "Close",
  loading: "Loading"
};

function InviteMemberDialog({
  open,
  onOpenChange,
  roles,
  accessModes,
  defaultAccessMode,
  canChooseAccess = true,
  fixedAccess = {},
  labels: labelOverrides,
  onSubmit
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: InviteMemberOption[];
  accessModes: InviteMemberOption[];
  defaultAccessMode: string;
  /** false keeps the access at its default, e.g. for someone who can only read. */
  canChooseAccess?: boolean;
  /** Roles whose access is fixed, by role: the access field hides and takes this value. */
  fixedAccess?: Record<string, string>;
  labels?: Partial<InviteMemberLabels>;
  /** Validates and sends. Return field errors to keep the dialog open with them shown. */
  onSubmit: (values: InviteMemberValues) => Promise<InviteMemberResult>;
}) {
  const labels = { ...defaultLabels, ...labelOverrides };
  const blank = React.useMemo(
    (): InviteMemberValues => ({ role: "", accessMode: defaultAccessMode, firstName: "", lastName: "", email: "" }),
    [defaultAccessMode]
  );
  const [values, setValues] = React.useState(blank);
  const [errors, setErrors] = React.useState<InviteMemberErrors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValues(blank);
      setErrors({});
    }
  }
  const ids = {
    role: React.useId(),
    accessMode: React.useId(),
    firstName: React.useId(),
    lastName: React.useId(),
    email: React.useId()
  };

  const change = (field: keyof InviteMemberValues, value: string) => {
    setValues((current) => {
      const next = { ...current, [field]: value };
      if (field === "role" && fixedAccess[value]) next.accessMode = fixedAccess[value];
      return next;
    });
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const submit = async () => {
    setSubmitting(true);
    const result = await onSubmit(values);
    setSubmitting(false);
    if (result.ok) onOpenChange(false);
    else setErrors(result.errors ?? {});
  };

  const textField = (field: "firstName" | "lastName" | "email", type: "text" | "email", autoComplete: string) => (
    <Field data-invalid={errors[field] ? true : undefined}>
      <FieldLabel htmlFor={ids[field]}>{labels[field]}</FieldLabel>
      <Input
        id={ids[field]}
        type={type}
        autoComplete={autoComplete}
        value={values[field]}
        placeholder={labels[`${field}Placeholder`]}
        aria-invalid={errors[field] ? true : undefined}
        onChange={(event) => change(field, event.target.value)}
      />
      <FieldError>{errors[field]}</FieldError>
    </Field>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent closeLabel={labels.close} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="typo-h5">{labels.title}</DialogTitle>
          <DialogDescription className="sr-only">{labels.description}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <FieldGroup className="gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {textField("firstName", "text", "given-name")}
              {textField("lastName", "text", "family-name")}
            </div>
            {textField("email", "email", "email")}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field data-invalid={errors.role ? true : undefined}>
                <FieldLabel htmlFor={ids.role}>{labels.role}</FieldLabel>
                <Select
                  items={roles}
                  value={values.role || null}
                  onValueChange={(value) => change("role", String(value ?? ""))}
                >
                  <SelectTrigger id={ids.role} className="w-full" aria-invalid={errors.role ? true : undefined}>
                    <SelectValue placeholder={labels.rolePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError>{errors.role}</FieldError>
              </Field>
              {!fixedAccess[values.role] && (
                <Field data-disabled={!canChooseAccess || undefined}>
                  <FieldLabel htmlFor={ids.accessMode}>{labels.access}</FieldLabel>
                  <Select
                    items={accessModes}
                    value={values.accessMode}
                    disabled={!canChooseAccess}
                    onValueChange={(value) => change("accessMode", String(value ?? ""))}
                  >
                    <SelectTrigger id={ids.accessMode} className="w-full">
                      <SelectValue placeholder={labels.accessPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {accessModes.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value}>
                          {mode.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          </FieldGroup>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              shape="pill"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              {labels.cancel}
            </Button>
            <Button type="submit" shape="pill" disabled={submitting}>
              {submitting && <Spinner data-icon="inline-start" aria-label={labels.loading} />}
              {labels.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export {
  InviteMemberDialog,
  type InviteMemberErrors,
  type InviteMemberLabels,
  type InviteMemberOption,
  type InviteMemberResult,
  type InviteMemberValues
};
